/**
 * Skylab API — List OAuth'd users for a school.
 *
 * GET /internal/skylab/users/:schoolId
 *
 * Super-admin-only. Lists the users who have completed a Blackbaud SKY OAuth
 * handshake for the given school, with their roles, admin scope, and live
 * token state. This same payload drives the Skylab Users tab (table) and the
 * Explorer's "Acting-as" combobox (U4) — every Explorer call goes out as one
 * of these users.
 *
 * Type names are prefixed `Skylab*` because the root shared-types barrel
 * re-exports every `api/*` module with `export *`; the prefix avoids
 * collisions with the generic `users` contracts.
 */

import { z } from 'zod'

/**
 * Live state of a user's Blackbaud token, derived at read time:
 * - `active`   — access token present and unexpired
 * - `idle`     — access token expired but a refresh token is available
 * - `expired`  — refresh grant revoked/disabled; user must reauthenticate
 */
export const SkylabTokenStatusSchema = z.enum(['active', 'idle', 'expired'])
export type SkylabTokenStatus = z.infer<typeof SkylabTokenStatusSchema>

export const SkylabOAuthUserSchema = z.object({
  /** UsersOnSchools.publicId for the active school (the "Act as" actor id). */
  id: z.string().min(1),
  /** User.publicId — stable user identity across schools. */
  userId: z.string().min(1),
  name: z.string(),
  email: z.string().nullable(),
  /** Display role labels (e.g. "Faculty", "School Admin", "Parent"). */
  roles: z.array(z.string()),
  /** True when the user has School Admin scope for this school. */
  isAdmin: z.boolean(),
  tokenStatus: SkylabTokenStatusSchema,
  /** True when a non-disabled refresh token exists (recoverable). */
  refreshAvailable: z.boolean(),
  /** Access-token expiry, ISO 8601, or null when unknown. */
  expiresAt: z.string().nullable(),
  /** Last token activity (proxy for "last seen"), ISO 8601, or null. */
  lastSeenAt: z.string().nullable()
})
export type SkylabOAuthUser = z.infer<typeof SkylabOAuthUserSchema>

export const SkylabUsersResponseSchema = z.object({
  data: z.array(SkylabOAuthUserSchema),
  /** Total OAuth'd users returned. */
  count: z.number().int().nonnegative(),
  /** Subset of `count` with admin scope. */
  adminCount: z.number().int().nonnegative()
})
export type SkylabUsersResponse = z.infer<typeof SkylabUsersResponseSchema>
